import type React from "react";
import { useCallback, useEffect, useState } from "react";
import type { SalesPipelineItem } from "../../../../hooks/use-sales-pipelines-api";
import { useTalents } from "../../../../hooks/use-talents-api";
import { useWorkspaceMembers } from "../../../../hooks/use-workspace-members-api";
import { getStageLabel } from "../../utils";

const STAGES = [
  "preparation",
  "proposed",
  "doc_screening",
  "interview",
  "offer",
] as const;

type PipelineFormData = {
  talentId: string;
  clientCompany: string;
  matterTitle: string;
  stage: string;
  proposedReward: string;
  nextActionDate: string;
  nextActionMemo: string;
  assignedTo: string;
  notes: string;
};

const INITIAL_FORM: PipelineFormData = {
  talentId: "",
  clientCompany: "",
  matterTitle: "",
  stage: "preparation",
  proposedReward: "",
  nextActionDate: "",
  nextActionMemo: "",
  assignedTo: "",
  notes: "",
};

type PipelineFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  submitting: boolean;
  initialData?: SalesPipelineItem;
};

/**
 * 営業進捗の作成・編集モーダル
 * @param {object} props コンポーネントプロパティ
 * @param {boolean} props.isOpen モーダルの表示状態
 * @param {Function} props.onClose 閉じるコールバック
 * @param {Function} props.onSubmit 送信コールバック
 * @param {boolean} props.submitting 送信中フラグ
 * @param {SalesPipelineItem} [props.initialData] 編集時の既存データ
 * @return {React.ReactElement | null} モーダル要素
 */
export const PipelineFormModal: React.FC<PipelineFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  submitting,
  initialData,
}) => {
  const isEditMode = !!initialData;
  const [form, setForm] = useState<PipelineFormData>(() =>
    initialData
      ? {
          talentId: initialData.talentId,
          clientCompany: initialData.clientCompany ?? "",
          matterTitle:
            initialData.matterTitle || initialData.focusMatterTitle || "",
          stage: initialData.stage,
          proposedReward:
            initialData.proposedReward != null
              ? String(initialData.proposedReward / 10000)
              : "",
          nextActionDate: initialData.nextActionDate ?? "",
          nextActionMemo: initialData.nextActionMemo ?? "",
          assignedTo: initialData.assignedTo ?? "",
          notes: initialData.notes ?? "",
        }
      : INITIAL_FORM,
  );
  const { talents } = useTalents({ limit: 100, isActive: true });
  const { members } = useWorkspaceMembers();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value } = e.target;
      setForm(prev => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.talentId) return;
      await onSubmit({
        talentId: form.talentId,
        clientCompany: form.clientCompany || undefined,
        matterTitle: form.matterTitle || undefined,
        stage: form.stage,
        proposedReward: form.proposedReward
          ? Math.round(Number(form.proposedReward) * 10000)
          : undefined,
        nextActionDate: form.nextActionDate || undefined,
        nextActionMemo: form.nextActionMemo || undefined,
        assignedTo: form.assignedTo || undefined,
        notes: form.notes || undefined,
      });
    },
    [form, onSubmit],
  );

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box w-11/12 max-w-lg">
        <h3 className="font-bold text-lg mb-4">
          {isEditMode ? "営業進捗を編集" : "営業進捗を追加"}
        </h3>
        <form onSubmit={handleSubmit}>
          {/* タレント選択 */}
          <div className="form-control mb-3">
            <label className="label" htmlFor="pl-talentId">
              <span className="label-text">
                タレント <span className="text-error">*</span>
              </span>
            </label>
            <select
              id="pl-talentId"
              name="talentId"
              className="select select-bordered select-sm"
              value={form.talentId}
              onChange={handleChange}
              required
              disabled={isEditMode}
            >
              <option value="">選択してください</option>
              {talents.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          {/* 案件情報 */}
          <div className="form-control mb-3">
            <label className="label" htmlFor="pl-matterTitle">
              <span className="label-text">案件タイトル</span>
            </label>
            <input
              id="pl-matterTitle"
              name="matterTitle"
              type="text"
              className="input input-bordered input-sm"
              value={form.matterTitle}
              onChange={handleChange}
            />
          </div>

          <div className="form-control mb-3">
            <label className="label" htmlFor="pl-clientCompany">
              <span className="label-text">企業名</span>
            </label>
            <input
              id="pl-clientCompany"
              name="clientCompany"
              type="text"
              className="input input-bordered input-sm"
              value={form.clientCompany}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="form-control">
              <label className="label" htmlFor="pl-stage">
                <span className="label-text">ステージ</span>
              </label>
              <select
                id="pl-stage"
                name="stage"
                className="select select-bordered select-sm"
                value={form.stage}
                onChange={handleChange}
              >
                {STAGES.map(s => (
                  <option key={s} value={s}>
                    {getStageLabel(s)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control">
              <label className="label" htmlFor="pl-proposedReward">
                <span className="label-text">提案単価（万円）</span>
              </label>
              <input
                id="pl-proposedReward"
                name="proposedReward"
                type="number"
                className="input input-bordered input-sm"
                value={form.proposedReward}
                onChange={handleChange}
                min={0}
              />
            </div>
          </div>

          {/* 次アクション */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="form-control">
              <label className="label" htmlFor="pl-nextActionDate">
                <span className="label-text">次アクション日</span>
              </label>
              <input
                id="pl-nextActionDate"
                name="nextActionDate"
                type="date"
                className="input input-bordered input-sm"
                value={form.nextActionDate}
                onChange={handleChange}
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="pl-nextActionMemo">
                <span className="label-text">次アクション内容</span>
              </label>
              <input
                id="pl-nextActionMemo"
                name="nextActionMemo"
                type="text"
                className="input input-bordered input-sm"
                value={form.nextActionMemo}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-control mb-3">
            <label className="label" htmlFor="pl-assignedTo">
              <span className="label-text">担当者</span>
            </label>
            <select
              id="pl-assignedTo"
              name="assignedTo"
              className="select select-bordered select-sm"
              value={form.assignedTo}
              onChange={handleChange}
            >
              <option value="">未指定</option>
              {members.map(m => (
                <option key={m.userId} value={m.userId}>
                  {m.familyName} {m.givenName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-control mb-4">
            <label className="label" htmlFor="pl-notes">
              <span className="label-text">メモ</span>
            </label>
            <textarea
              id="pl-notes"
              name="notes"
              className="textarea textarea-bordered textarea-sm"
              rows={2}
              value={form.notes}
              onChange={handleChange}
            />
          </div>

          {/* Actions */}
          <div className="modal-action">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onClose}
              disabled={submitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={submitting || !form.talentId}
            >
              {submitting ? (
                <span className="loading loading-spinner loading-xs" />
              ) : isEditMode ? (
                "保存"
              ) : (
                "追加"
              )}
            </button>
          </div>
        </form>
      </div>
      <div
        className="modal-backdrop"
        onClick={() => !submitting && onClose()}
      />
    </div>
  );
};
